import { WithCopy } from '@scale/frontend-lib/sensor-fusion';
import { ISensorFusionStageInstanceLookupDto } from '@scale/sensor-fusion-shared';

import { LookupIdLink } from '../common';
import { CORP_LOOKUP_PATH, REMOADMIN_LOOKUP_PATH } from '../constants';
import { useSensorFusionLookupContext } from '../contexts/SensorFusionLookupContext';
import { SubstageInstanceListItem } from 'react-scale/dashboard.scale.com/components/SensorFusionLookup/common/SubstageInstanceListItem';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import { useMemo } from 'react';

type StageInstanceOverviewProps = {
  stageInstance: ISensorFusionStageInstanceLookupDto;
};

const StageInstanceOverview = ({ stageInstance }: StageInstanceOverviewProps) => {
  const { stageDefinition, substageInstances, project } = stageInstance;

  const { scaleDashboardFrontendUrl, remotasksFrontendUrl } = useSensorFusionLookupContext() ?? {};
  const { isRemoAdmin } = useRemoAdminCtx();

  const toolUrl = useMemo(() => {
    if (isRemoAdmin) {
      return `${remotasksFrontendUrl ?? ''}/${REMOADMIN_LOOKUP_PATH}`;
    }
    return `${scaleDashboardFrontendUrl ?? ''}/${CORP_LOOKUP_PATH}`;
  }, [isRemoAdmin, remotasksFrontendUrl, scaleDashboardFrontendUrl]);

  return (
    <div className="border-solid border border-neutral-200 px-4 py-4 my-4 w-full overflow-wrap-anywhere">
      <h4 className="font-bold text-lg">Overview</h4>
      <div className="flex flex-col py-1">
        <div className="flex flex-row justify-between">
          <div className="flex flex-col gap-2">
            <div>
              <span className="font-bold">Unified Task: </span>
              <LookupIdLink className="inline-block" id={stageInstance.taskId} />
            </div>
            <div>
              <span className="font-bold">Project: </span>
              {`${project.name} `}
              <WithCopy className="inline-block text-info-700" copyText={project.id}>
                <a href={`${toolUrl}/${project.id}`}>{project.id}</a>
              </WithCopy>
            </div>
            <div>
              <span className="font-bold">Status: </span>
              {stageInstance.status}
            </div>
            <div>
              <span className="font-bold">Stage Definition: </span>
              {`${stageDefinition.name} `}
              <WithCopy className="inline-block text-info-700" copyText={stageDefinition.id}>
                <a href={`${toolUrl}/${stageDefinition.id}`}>{stageDefinition.id}</a>
              </WithCopy>
            </div>
            <div>
              <span className="font-bold">Substage Instances:</span>
              {substageInstances.length ? (
                <ul className="ml-2">
                  {substageInstances.map((substageInstance, index) => (
                    <SubstageInstanceListItem
                      substageInstance={substageInstance}
                      key={`substage_list_item_${index}`}
                    />
                  ))}
                </ul>
              ) : (
                <span className="ml-2">None</span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1 w-1/3">
            {/* TODO: Open in debug */}
            {/* <div className="flex flex-col">TODO: Open in debug</div> */}
            {stageInstance.params.sceneId || !stageInstance.params.sceneUrl ? (
              <div>
                <span className="font-bold">Scene ID: </span>
                {stageInstance.params.sceneId ? (
                  <LookupIdLink className="inline-block" id={stageInstance.params.sceneId} />
                ) : (
                  'None'
                )}
              </div>
            ) : (
              <WithCopy className="inline-block" copyText={stageInstance.params.sceneUrl}>
                <a className="text-info-700" href={stageInstance.params.sceneUrl}>
                  Scene URL
                </a>
              </WithCopy>
            )}
            <div>
              <span className="font-bold">Created At: </span>
              {new Date(stageInstance.createdAt).toLocaleString()}
            </div>
            {stageInstance.completedAt && (
              <div>
                <span className="font-bold">Completed At: </span>
                {new Date(stageInstance.completedAt).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      </div>
      <style jsx>{`
        .overflow-wrap-anywhere {
          overflow-wrap: anywhere;
        }
      `}</style>
    </div>
  );
};

export default StageInstanceOverview;
